<script lang="ts">
  import type { PlanId, Cadence, Plans } from './types';

  interface Props {
    selectedPlan: PlanId;
    cadence: Cadence;
    acceptTerms: boolean;
    isSubmitting: boolean;
    plans: Plans;
    onSubmit: (e: Event) => void;
    onBack: () => void;
    onPlanSelect: (planId: PlanId) => void;
    onCadenceSelect: (cadence: Cadence) => void;
    onTermsChange: (accepted: boolean) => void;
  }

  let {
    selectedPlan,
    cadence,
    acceptTerms,
    isSubmitting,
    plans,
    onSubmit,
    onBack,
    onPlanSelect,
    onCadenceSelect,
    onTermsChange,
  }: Props = $props();
</script>

<form class="signup-form" onsubmit={onSubmit}>
  <div class="form-section">
    <h3>Select Your Plan</h3>
    <div class="plan-options">
      {#each Object.entries(plans) as [planId, plan]}
        <button
          type="button"
          class="plan-option"
          class:selected={selectedPlan === planId}
          onclick={() => onPlanSelect(planId as PlanId)}
        >
          <div class="plan-header">
            <strong>{plan.name}</strong>
            <span class="headline">{plan.headline}</span>
          </div>
          <p>{plan.summary}</p>
          <div class="price-row">
            <span>{plan.monthly}/mo</span>
            <span class="annual">or {plan.annual}/mo billed annually</span>
          </div>
        </button>
      {/each}
    </div>
  </div>

  <div class="form-section">
    <h3>Billing Cadence</h3>
    <div class="cadence-toggle">
      <button
        type="button"
        class:active={cadence === 'monthly'}
        onclick={() => onCadenceSelect('monthly')}
      >
        Monthly
      </button>
      <button
        type="button"
        class:active={cadence === 'annual'}
        onclick={() => onCadenceSelect('annual')}
      >
        Annual (save 10%)
      </button>
    </div>
  </div>

  <div class="form-section">
    <div class="no-addons-note">
      Add-ons will be available soon. Contact <a href="mailto:support@equosnine.com">support@equosnine.com</a> if you need something specific before launch.
    </div>
  </div>

  <div class="form-section">
    <label class="terms">
      <input
        type="checkbox"
        checked={acceptTerms}
        onchange={(e) => onTermsChange(e.currentTarget.checked)}
      />
      <span>
        I agree to the EQUOS billing terms and understand the trial
        converts to a paid subscription after 14 days unless cancelled.
      </span>
    </label>
  </div>

  <div class="form-actions two-buttons">
    <button
      type="button"
      class="btn-secondary"
      onclick={onBack}
      disabled={isSubmitting}
    >
      Back
    </button>
    <button type="submit" class="btn-primary" disabled={isSubmitting}>
      Continue to Payment
    </button>
  </div>
</form>

<style>
  .signup-form {
    background: white;
    border-radius: 16px;
    padding: clamp(1.5rem, 3vw, 2.5rem);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  }

  .form-section {
    margin-bottom: 2rem;
  }

  .form-section:last-of-type {
    margin-bottom: 1.5rem;
  }

  .form-section h3 {
    color: #1f2937;
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #e5e7eb;
  }

  /* Plan Options */
  .plan-options {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
  }

  .plan-option {
    border: 2px solid var(--color-gray-200);
    border-radius: 14px;
    padding: 1.25rem;
    text-align: left;
    background: white;
    cursor: pointer;
    transition: all 0.25s ease;
  }

  .plan-option:hover {
    border-color: #4A7DFF;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(74, 125, 255, 0.15);
  }

  .plan-option.selected {
    border-color: #4A7DFF;
    box-shadow: 0 0 0 3px rgba(74, 125, 255, 0.15);
    background: linear-gradient(135deg, rgba(74, 125, 255, 0.04) 0%, rgba(107, 92, 231, 0.04) 100%);
  }

  .plan-option .plan-header {
    display: flex;
    flex-direction: column;
  }

  .plan-option .plan-header strong {
    color: var(--color-gray-800);
    font-size: 1.1rem;
  }

  .plan-option .headline {
    color: var(--color-gray-400);
    font-size: 0.85rem;
  }

  .plan-option p {
    color: var(--color-gray-500);
    font-size: 0.9rem;
    margin: 0.5rem 0;
    line-height: 1.4;
  }

  .plan-option .price-row {
    display: flex;
    flex-direction: column;
    font-weight: 600;
    color: var(--color-gray-800);
    margin-top: 0.5rem;
  }

  .plan-option .price-row .annual {
    font-size: 0.8rem;
    font-weight: 400;
    color: var(--color-gray-400);
  }

  /* Cadence Toggle */
  .cadence-toggle {
    display: inline-flex;
    gap: 0.5rem;
  }

  .cadence-toggle button {
    padding: 0.75rem 1.5rem;
    border: 2px solid var(--color-gray-200);
    border-radius: 999px;
    background: transparent;
    color: var(--color-gray-700);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .cadence-toggle button:hover {
    border-color: #4A7DFF;
  }

  .cadence-toggle button.active {
    background: linear-gradient(135deg, #4A7DFF 0%, #6B5CE7 100%);
    color: white;
    border-color: transparent;
    box-shadow: 0 4px 12px rgba(74, 125, 255, 0.35);
  }

  /* Terms */
  .no-addons-note {
    padding: 0.875rem 1rem;
    border-radius: 10px;
    background: #f8f9fc;
    color: var(--color-gray-500);
    font-size: 0.875rem;
    line-height: 1.5;
  }

  .no-addons-note a {
    color: #4A7DFF;
    text-decoration: none;
    font-weight: 600;
  }

  .no-addons-note a:hover {
    text-decoration: underline;
  }

  .terms {
    display: flex;
    gap: 0.75rem;
    align-items: flex-start;
    color: var(--color-gray-600);
    font-size: 0.875rem;
    line-height: 1.5;
    cursor: pointer;
  }

  .terms input[type='checkbox'] {
    width: 1.25rem;
    height: 1.25rem;
    margin-top: 0.1rem;
    accent-color: #4A7DFF;
    flex-shrink: 0;
  }

  /* Form Actions */
  .form-actions {
    margin-top: 1.5rem;
  }

  .form-actions.two-buttons {
    display: flex;
    gap: 1rem;
  }

  .btn-primary {
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.9rem 1.5rem;
    background: linear-gradient(135deg, #4A7DFF 0%, #6B5CE7 100%);
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-primary:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(74, 125, 255, 0.35);
  }

  .btn-primary:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .btn-secondary {
    padding: 0.9rem 1.5rem;
    background: white;
    color: var(--color-gray-700);
    border: 2px solid var(--color-gray-200);
    border-radius: 10px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-secondary:hover:not(:disabled) {
    border-color: #4A7DFF;
    color: #4A7DFF;
  }

  .btn-secondary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 640px) {
    .plan-options {
      grid-template-columns: 1fr;
    }

    .cadence-toggle {
      flex-direction: column;
      width: 100%;
    }

    .cadence-toggle button {
      width: 100%;
      justify-content: center;
    }

    .form-actions.two-buttons {
      flex-direction: column-reverse;
    }

    .btn-secondary {
      width: 100%;
    }
  }
</style>
